/* eslint-disable react/destructuring-assignment */
import { Amplify, Auth, Hub } from 'aws-amplify';
import cryptoRandomString from 'crypto-random-string';
import { usePostHog } from 'posthog-js/react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setDefaultLocale } from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';
import { validateEmail } from '../components/_ui/modal/utils';
import { Api } from '../services/base.api';
import { httpRawGet } from '../services/http/http.service';
import { getLogger } from '../services/logging/logger';
import { useLogger } from '../services/logging/logger-context';
import { i18n } from '../services/translation/i18n';
import { getWhiteLabellingCompanyName } from '../stores/selectors/env.selector';
import { pushUserProperties } from '../utils/GoogleAnalytics';

export enum USER_ROLE {
  SUPPORT = 'SUPPORT',
  NORMAL = 'NORMAL',
}

export interface UserAttributes {
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    country?: string;
    isoCode?: string;
    postalCode?: string;
    province?: string;
  };
  'custom:legacy_support_da'?: string;
  'custom:stable_user_id'?: string;
  email?: string;
  email_verified?: boolean;
  family_name?: string;
  given_name?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  profile?: {
    lastActiveCompanyId?: string;
    sitehostDashboardLanguage?: string;
    hsContactId?: string;
  };
  sub?: string;
}

export interface User {
  id?: string;
  username?: string;
  attributes: UserAttributes;
}

interface DeploymentOauthConfig {
  domain: string;
  redirect_uri: string;
  sso_domain_provider_map: Record<string, string>;
  sso_otp_domain_allowlist: string[];
}

interface AuthProviderOauthConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  ssoDomainProviderMap: Record<string, string>;
  ssoOtpDomainAllowlist: string[];
}

type ContextType = {
  signIn: Function;
  signInFederated: Function;
  signOut: Function;
  isUserFederated: Function;
  signUp: Function;
  resendCode: Function;
  verifyCode: Function;
  setToken: Function;
  cleanError: Function;
  setCognitoUser: Function;
  handleUserChange: Function;
  setLoading: Function;
  updateUserLanguage: Function;
  updateCompanyId: Function;
  updateHubspotContactId: Function;
  hasSsoConfiguration: Function;
  domainHasSsoConfiguration: Function;
  allowSsoUserSignInByOtp: Function;
  cognitoUser: any;
  user: any;
  requestedUser: string;
  role: USER_ROLE | undefined;
  token: string;
  scope: string;
  error: string;
  loading: boolean;
};

const initalState: ContextType = {
  signIn: () => {},
  signInFederated: () => {},
  signOut: () => {},
  isUserFederated: () => {},
  signUp: () => {},
  resendCode: () => {},
  verifyCode: () => {},
  setToken: () => {},
  cleanError: () => {},
  setCognitoUser: () => {},
  handleUserChange: () => {},
  updateUserLanguage: () => {},
  updateCompanyId: () => {},
  updateHubspotContactId: () => {},
  hasSsoConfiguration: () => {},
  domainHasSsoConfiguration: () => {},
  allowSsoUserSignInByOtp: () => {},
  setLoading: () => {},
  cognitoUser: {},
  loading: false,
  user: {
    attributes: {
      sub: '',
      email_verified: true,
      phone_number_verified: true,
      email: '',
      family_name: '',
      given_name: '',
      phone_number: '',
      profile: {
        sitehostDashboardLanguage: 'en-US',
        lastActiveCompanyId: '',
        acceptedSiteHostTerms: '',
      },
    },
    username: '',
  },
  requestedUser: '',
  role: USER_ROLE.NORMAL,
  token: '',
  scope: 'company',
  error: '',
};

const getEmailDomainFromUsername = (username: string | undefined | null) => {
  const emailParts = username?.split('@');
  return emailParts?.length === 2 ? emailParts[1] : '';
};

const transformUserInfo = (newUserinfo: any) => {
  if (!newUserinfo || !newUserinfo.attributes) {
    return newUserinfo;
  }

  let profile;
  if (newUserinfo.attributes?.profile) {
    profile = JSON.parse(newUserinfo.attributes.profile);
  }

  newUserinfo.attributes.profile = {
    ...profile, // Spread all existing fields first (preserves fields from other applications)
    lastActiveCompanyId: profile?.lastActiveCompanyId || '',
    acceptedSiteHostTerms: profile?.acceptedSiteHostTerms || '1.0',
    hsContactId: profile?.hsContactId || undefined,
  };

  return newUserinfo;
};

const useProvideAuth = (oauthConfig: AuthProviderOauthConfig) => {
  const [requestedUser, setRequestedUser] = useState('');
  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState('');
  const scope = 'company';
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const dispatch = useDispatch();
  const posthog = usePostHog();

  const handleUserChange = useCallback(
    (newUser: User) => {
      if (!newUser) {
        return;
      }
      if (newUser.attributes['custom:stable_user_id']) {
        posthog.identify(newUser.attributes['custom:stable_user_id']);
        // Add some posthog user properties for identify
      }

      setUser(newUser);
    },
    [posthog],
  );

  const role = useMemo(() => {
    return user?.attributes['custom:legacy_support_da'] === 'ALLOW'
      ? USER_ROLE.SUPPORT
      : USER_ROLE.NORMAL;
  }, [user]);
  const whiteLabelCompanyName: any = useSelector(getWhiteLabellingCompanyName);

  const signIn = (loginUsername: string, sendLoginCodeToEmail: boolean) => {
    if (!loginUsername) {
      setError('Please enter your email or phone number.');
      return;
    }

    setRequestedUser(loginUsername);

    Auth.signIn(loginUsername.trim())
      .then((data) => {
        Auth.sendCustomChallengeAnswer(data, loginUsername.trim(), {
          isEmail: `${sendLoginCodeToEmail}`,
        }).then((newCognitoUser) => {
          setCognitoUser(newCognitoUser);
        });
      })
      .catch((e: any) => setError(e.message));
  };

  const signInFederated = (loginUsername: string, sendLoginCodeToEmail: boolean) => {
    if (!loginUsername) {
      setError('Please enter your email or phone number.');
      return;
    }

    setRequestedUser(loginUsername);

    const emailDomain = getEmailDomainFromUsername(loginUsername);
    const provider = oauthConfig.ssoDomainProviderMap[emailDomain];

    if (!provider) {
      signIn(loginUsername, sendLoginCodeToEmail);
    } else {
      Auth.federatedSignIn({
        customProvider: provider,
      });
    }
  };

  const signUp = async (
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    siteHostTermsOfServiceVersion: string,
    hsContactId?: string,
  ) => {
    const userProfile = {
      acceptedSiteHostTerms: siteHostTermsOfServiceVersion,
      sitehostDashboardLanguage: i18n.language || 'en-US',
      hsContactId,
    };
    try {
      const attributes: any = {};
      attributes.given_name = firstName;
      attributes.family_name = lastName;
      attributes.email = email.trim();
      if (phoneNumber) attributes.phone_number = phoneNumber;
      attributes.profile = JSON.stringify(userProfile);
      await Auth.signUp({
        username: cryptoRandomString({ length: 16, type: 'alphanumeric' }),
        password: cryptoRandomString({ length: 120, type: 'ascii-printable' }), // May have a chance to not comply with the Cognito password rules
        attributes: attributes,
      });
    } catch (e: any) {
      setError(e.message);
    }
  };

  const updateUserLanguage = async (lang: string) => {
    const newProfile = {
      ...user.attributes.profile,
      sitehostDashboardLanguage: lang,
    };
    Auth.updateUserAttributes(cognitoUser, {
      profile: JSON.stringify(newProfile),
    });
    user.attributes.profile = newProfile;
    handleUserChange(user);
    i18n.changeLanguage(lang);
    if (document) {
      document.documentElement.lang = lang;
    }
    setDefaultLocale(lang);
  };

  const updateCompanyId = async (companyId: string, cleanCache: boolean = true) => {
    // Skip if already set to avoid loops
    if (user?.attributes?.profile?.lastActiveCompanyId === companyId) {
      return;
    }

    const newProfile = {
      ...user.attributes.profile,
      lastActiveCompanyId: companyId,
    };
    user.attributes.profile = newProfile;
    // cleanCache is for deeplinking, when users manually refresh the page, we don't need to clean cache again
    if (cleanCache) {
      dispatch(Api.util.resetApiState());
    }
    handleUserChange(user);
    await Auth.updateUserAttributes(cognitoUser, {
      profile: JSON.stringify(newProfile),
    });
  };

  // This does not validate that the Hubspot Contact ID exists
  // and permits any string to be set as the Hubspot Contact ID.
  const updateHubspotContactId = async (hubspotContactId: string) => {
    const newProfile = {
      ...user.attributes.profile,
      hsContactId: hubspotContactId,
    };
    user.attributes.profile = newProfile;

    handleUserChange(user);

    await Auth.updateUserAttributes(cognitoUser, {
      profile: JSON.stringify(newProfile),
    });
  };

  // Currently our system doesn't send text to non-NorthAmerican numbers
  // Hence tif username is a phone number with a country code other than '1', send login code to email.
  const resendCode = () => {
    Auth.signIn(cognitoUser.username)
      .then((data) => {
        Auth.sendCustomChallengeAnswer(data, cognitoUser.username, {
          isEmail: `${
            validateEmail(cognitoUser.attributes.email) || !cognitoUser.username.startsWith('+1')
          }`,
        }).then((newCognitoUser) => {
          setCognitoUser(newCognitoUser);
          setLoading(false);
        });
      })
      .catch((e: any) => {
        setError(e.message);
        setLoading(false);
      });
  };

  const verifyCode = async (code: string) => {
    Auth.sendCustomChallengeAnswer(cognitoUser, code).catch((e) => {
      setError(`verifyCode failed: ${e.message}, ${e}`);
      setLoading(false);
    });
  };

  const isUserFederated = () => {
    const identities = JSON.parse(user?.attributes?.identities || '[]');

    return identities.some((identity: any) => identity.providerType === 'SAML');
  };

  const signOut = () => {
    try {
      Auth.signOut().catch((e) => {});
      dispatch(Api.util.resetApiState());
      // Retain the language value from localstorage even after signout (BF-1375)
      const lang = localStorage.getItem('language') || 'en-US';

      // Retain hideModal- key/value pairs even after signout (ON-473)
      const matchingKeys = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key?.includes('hideModal-')) {
          matchingKeys.push({ key, value: localStorage.getItem(key) || false });
        }
      }
      localStorage.clear();
      localStorage.setItem('language', lang);
      matchingKeys.forEach((item) => {
        localStorage.setItem(item.key, item.value?.toString());
      });
      setCognitoUser(null);
      posthog.reset();
      setToken('');
    } catch (e: any) {
      setError(`error signing out: ${e.message}`);
    } finally {
      if (isUserFederated() && oauthConfig.domain) {
        window.location.replace(
          `https://${oauthConfig.domain}/logout?client_id=${oauthConfig.clientId}&logout_uri=${oauthConfig.redirectUri}&response_type=code&redirect_uri=${oauthConfig.redirectUri}`,
        );
      } else {
        window.location.replace(`${oauthConfig.redirectUri}`);
      }
    }
  };

  const hasSsoConfiguration = () => {
    return Object.keys(oauthConfig.ssoDomainProviderMap).length > 0;
  };

  const domainHasSsoConfiguration = (email: string) => {
    const domain = getEmailDomainFromUsername(email);
    return domain && Object.keys(oauthConfig.ssoDomainProviderMap).includes(domain);
  };

  const allowSsoUserSignInByOtp = (email: string) => {
    const domain = getEmailDomainFromUsername(email);
    // This is safe in the frontend because ultimately the Cognito lambdas will validate OTP sign-in domains.
    return oauthConfig.ssoOtpDomainAllowlist.includes(domain);
  };

  useEffect(() => {
    const logger = getLogger();
    const unsubscribe = Hub.listen('auth', (data) => {
      switch (data.payload.event) {
        case 'signIn':
          setCognitoUser(data.payload.data);

          Auth.currentUserInfo()
            .then((signInUserInfo) => {
              const transformedUser = transformUserInfo(signInUserInfo);
              handleUserChange(transformedUser);
              // Set acceptedSiteHostTerms to 1.0 for users that are already registered in our system
              // before PM-1738 was implemented
              Auth.updateUserAttributes(data.payload.data, {
                profile: JSON.stringify({
                  ...transformedUser.attributes.profile,
                  acceptedSiteHostTerms: transformedUser.attributes.profile?.acceptedSiteHostTerms,
                }),
              });
              try {
                pushUserProperties(transformedUser.username, {
                  company: whiteLabelCompanyName,
                });
              } catch (e: any) {
                logger.warn('Error pushing user properties', e as Error);
              }
            })
            .catch((e) => {});
          break;
        case 'signUp':
          break;
        case 'signOut':
          break;
        case 'signIn_failure':
          break;
        case 'tokenRefresh':
          break;
        case 'tokenRefresh_failure':
          break;
        case 'configured':
          break;
        default:
          break;
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [handleUserChange, whiteLabelCompanyName]);

  const cleanError = () => setError('');
  // Return the user object and auth methods
  return {
    user,
    requestedUser,
    cognitoUser,
    role,
    token,
    scope,
    error,
    loading,
    signIn,
    signInFederated,
    signUp,
    signOut,
    isUserFederated,
    setToken,
    setLoading,
    resendCode,
    verifyCode,
    cleanError,
    setCognitoUser,
    handleUserChange,
    updateUserLanguage,
    updateCompanyId,
    updateHubspotContactId,
    hasSsoConfiguration,
    domainHasSsoConfiguration,
    allowSsoUserSignInByOtp,
  };
};

const authContext = createContext(initalState);

export const useAuth = () => {
  return useContext(authContext);
};

export const AuthProvider = (props: any) => {
  const { logger } = useLogger();
  const { children } = props;
  const [isConfigInited, setIsConfigInited] = useState(false);
  const [deploymentConfig, setDeploymentConfig] = useState<null | {
    region: string;
    userPoolId: string;
    clientId: string;
    oauth: DeploymentOauthConfig;
  }>(null);

  useEffect(() => {
    const fetchCognitoConfig = async () => {
      const dep = await httpRawGet('/deployment/cognito').catch((e) => e);
      setDeploymentConfig(dep);
    };
    fetchCognitoConfig();
  }, []);

  const currentUrlRoot = window.location.origin;

  const auth = useProvideAuth({
    domain: deploymentConfig?.oauth?.domain || '',
    redirectUri: currentUrlRoot || '',
    ssoDomainProviderMap: deploymentConfig?.oauth?.sso_domain_provider_map || {},
    ssoOtpDomainAllowlist: deploymentConfig?.oauth?.sso_otp_domain_allowlist || [],
    clientId: deploymentConfig?.clientId || '',
  });
  const { setCognitoUser, handleUserChange } = auth;
  const setupCognito = useCallback(async () => {
    const oauth =
      deploymentConfig && deploymentConfig.oauth && deploymentConfig.oauth.domain
        ? {
            domain: deploymentConfig?.oauth.domain,
            redirectSignIn: currentUrlRoot,
            redirectSignOut: currentUrlRoot,
            scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
            responseType: 'code',
          }
        : null;

    Amplify.configure({
      Auth: {
        region: deploymentConfig?.region,
        userPoolId: deploymentConfig?.userPoolId,
        userPoolWebClientId: deploymentConfig?.clientId,
        mandatorySignIn: false,
        authenticationFlowType: 'CUSTOM_AUTH',
      },
      oauth,
    });

    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      setCognitoUser(cognitoUser);
      const userInfo = await Auth.currentUserInfo();

      const transformed = transformUserInfo(userInfo);
      if (transformed) {
        handleUserChange(transformed);
      }
    } catch (e) {
      logger.error('get auth user error:', e as Error);
    }

    setIsConfigInited(true);
  }, [currentUrlRoot, deploymentConfig, setCognitoUser, handleUserChange]);
  useEffect(() => {
    if (!deploymentConfig) {
      return;
    }
    setupCognito();
  }, [deploymentConfig, setupCognito]);
  if (!isConfigInited) {
    return null;
  }
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};
